// ============================================================
// FILE: MyReferrals.js
// LOCATION: C:\gardenmarket\web\src\components\MyReferrals.js
// ============================================================
// PURPOSE:
//   Shows a gardener who they have brought onto the platform and
//   what it has earned them.
//
// THE OFFER:
//   When a gardener you introduced buys credits, you get 10% of
//   that purchase as credit. It runs for as long as they keep
//   buying, with no cap.
//
// WHY THE HOLD MATTERS TO THE UI:
//   A bonus does not pay out until the gardener you referred has
//   completed their first job. That is a deliberate fraud control
//   — without it, anyone could register a second account, top it
//   up with their own card, take the 10% and charge the payment
//   back. It has to be explained plainly here, because otherwise
//   "why is my $2 not in my balance" turns into a support call.
//
//   Hence pending and earned are shown as separate figures, never
//   summed into one number.
//
// SHARING:
//   /register?ref=CODE prefills the referral field. Asking another
//   gardener to remember a code and type it into the right box
//   loses most of them.
//
//   NOTE: this is a DIFFERENT link from MyBookingLink's /g/CODE,
//   which is for the gardener's own customers. Same code, two
//   audiences. The wording keeps them apart.
//
// COMPACT MODE:
//   compact renders just the link and the offer, for the profile
//   page, where it sits directly beneath MyBookingLink. Those two
//   links look almost identical and do opposite things — one
//   brings a customer who posts a job only you can see, the other
//   brings a gardener you earn from. Sending the wrong one to the
//   wrong person is the obvious failure, so side by side they
//   have to say plainly who each is for.
//
//   The stats and the list of people referred stay on the Credits
//   page. Showing the same full card twice would read as two
//   different things.
// ============================================================

import { useState, useEffect } from 'react';
import client from '../api/client';

function money(amount, currency = 'AUD') {
    const n = Math.abs(Number(amount) || 0);
    const symbol = currency === 'USD' ? 'US$' : currency === 'NZD' ? 'NZ$' : '$';
    return symbol + n.toFixed(2);
}

export default function MyReferrals({ compact = false }) {
    const [summary,   setSummary]   = useState(null);
    const [referrals, setReferrals] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [copied,    setCopied]    = useState(false);

    useEffect(() => {
        let cancelled = false;
        client.get('/gardeners/referrals')
            .then(r => {
                if (cancelled) return;
                setSummary(r.data.summary);
                setReferrals(r.data.referrals || []);
            })
            .catch(() => { /* non-fatal: the card just does not render */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading || !summary || !summary.referral_code) return null;

    const currency = summary.currency || 'AUD';
    const pct      = Math.round((Number(summary.bonus_rate) || 0.1) * 100);
    const url      = `${window.location.origin}/register?ref=${summary.referral_code}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // Clipboard needs HTTPS and a user gesture, and can
            // still fail. Select the text so they can copy it.
            const el = document.getElementById('gh-referral-link');
            if (el) {
                const range = document.createRange();
                range.selectNodeContents(el);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
    };

    const shareText =
        `I use GardenHand for gardening jobs. Sign up here and we both get a bit of credit: ${url}`;
    const smsHref = `sms:?&body=${encodeURIComponent(shareText)}`;

    const hasPending = Number(summary.total_pending) > 0;

    return (
        <div style={S.card}>
            <h3 style={S.title}>Refer another gardener</h3>

            <p style={S.blurb}>
                {compact && (
                    <>
                        <strong>For other gardeners, not your customers.</strong>{' '}
                    </>
                )}
                When a gardener you introduce buys credits, you get{' '}
                <strong>{pct}% of what they spend</strong> as credit on your own
                account — for as long as they keep buying.
            </p>

            <div style={S.linkRow}>
                <code id="gh-referral-link" style={S.link}>{url}</code>
            </div>

            <div style={S.actions}>
                <button
                    type="button"
                    onClick={copy}
                    style={copied ? S.btnCopied : S.btn}
                    className="gh-touch-target"
                >
                    {copied ? '✓ Copied' : 'Copy link'}
                </button>
                <a href={smsHref} style={S.btnSecondary} className="gh-touch-target">
                    Send by text
                </a>
            </div>

            <p style={S.fine}>
                Or they can type your code <strong>{summary.referral_code}</strong>{' '}
                when they sign up.
            </p>

            {/* On the profile the card stops here. Earnings live on
                the Credits page, next to the balance they top up. */}
            {compact && (
                <p style={S.fine}>
                    <a href="/gardener/credits" style={S.inlineLink}>
                        {Number(summary.gardeners_referred) > 0
                            ? `You've referred ${summary.gardeners_referred} gardener${Number(summary.gardeners_referred) === 1 ? '' : 's'} — see what you've earned →`
                            : 'See your referrals and earnings →'}
                    </a>
                </p>
            )}

            {/* Stats. Earned and pending stay separate on purpose —
                one is spendable now, the other is not. */}
            {!compact && (
            <div style={S.stats}>
                <Stat label="Gardeners referred" value={summary.gardeners_referred ?? 0} />
                <Stat label="Earned"  value={money(summary.total_earned,  currency)} />
                <Stat label="Pending" value={money(summary.total_pending, currency)} muted />
            </div>
            )}

            {!compact && hasPending && (
                <div style={S.note}>
                    Pending bonuses are released once the gardener you referred
                    completes their first job.
                </div>
            )}

            {!compact && referrals.length > 0 && (
                <div style={S.list}>
                    {referrals.map(r => (
                        <div key={r.gardener_id} style={S.row}>
                            <div>
                                <div style={S.name}>
                                    {r.first_name} {r.last_name || ''}
                                </div>
                                <div style={S.meta}>
                                    Joined {new Date(r.joined_at).toLocaleDateString()}
                                    {!r.has_completed_job && ' · no completed jobs yet'}
                                </div>
                            </div>
                            <div style={S.amounts}>
                                {Number(r.earned) > 0 && (
                                    <div style={S.earned}>{money(r.earned, currency)}</div>
                                )}
                                {Number(r.pending) > 0 && (
                                    <div style={S.pending}>
                                        {money(r.pending, currency)} pending
                                    </div>
                                )}
                                {Number(r.earned) === 0 && Number(r.pending) === 0 && (
                                    <div style={S.pending}>—</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!compact && referrals.length === 0 && (
                <p style={S.empty}>
                    You haven't referred anyone yet. Most gardeners know two or
                    three others who'd find this useful.
                </p>
            )}
        </div>
    );
}

function Stat({ label, value, muted }) {
    return (
        <div style={S.stat}>
            <div style={muted ? S.statValueMuted : S.statValue}>{value}</div>
            <div style={S.statLabel}>{label}</div>
        </div>
    );
}

const S = {
    card:         { background:'white', borderRadius:'10px', padding:'20px',
                    boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'16px' },
    title:        { fontSize:'1rem', fontWeight:'bold', color:'#2d6a4f',
                    margin:'0 0 10px' },
    blurb:        { fontSize:'0.88rem', color:'#555', lineHeight:1.6,
                    marginTop:0, marginBottom:'14px' },
    linkRow:      { background:'#f5f7f6', border:'1px solid #dde5e1',
                    borderRadius:'6px', padding:'10px 12px', marginBottom:'12px',
                    overflowX:'auto' },
    link:         { fontSize:'0.85rem', color:'#2d6a4f', fontFamily:
                    'ui-monospace, Menlo, Consolas, monospace', whiteSpace:'nowrap' },
    actions:      { display:'flex', gap:'8px', flexWrap:'wrap' },
    btn:          { padding:'10px 18px', backgroundColor:'#2d6a4f', color:'white',
                    border:'none', borderRadius:'6px', fontSize:'0.88rem',
                    fontWeight:600, cursor:'pointer' },
    btnCopied:    { padding:'10px 18px', backgroundColor:'#14532D', color:'white',
                    border:'none', borderRadius:'6px', fontSize:'0.88rem',
                    fontWeight:600, cursor:'pointer' },
    btnSecondary: { padding:'10px 18px', backgroundColor:'white', color:'#2d6a4f',
                    border:'1px solid #ddd', borderRadius:'6px', fontSize:'0.88rem',
                    fontWeight:600, textDecoration:'none', display:'inline-flex',
                    alignItems:'center' },
    fine:         { fontSize:'0.78rem', color:'#999', lineHeight:1.6,
                    marginTop:'12px', marginBottom:0 },
    inlineLink:   { color:'#2d6a4f', fontWeight:600, textDecoration:'none' },
    stats:        { display:'flex', gap:'20px', flexWrap:'wrap',
                    marginTop:'18px', paddingTop:'16px',
                    borderTop:'1px solid #eee' },
    stat:         { minWidth:'90px' },
    statValue:      { fontSize:'1.15rem', fontWeight:700, color:'#14532D' },
    statValueMuted: { fontSize:'1.15rem', fontWeight:700, color:'#999' },
    statLabel:    { fontSize:'0.72rem', color:'#888', textTransform:'uppercase',
                    letterSpacing:'0.04em', marginTop:'2px' },
    note:         { background:'#FEF9E7', border:'1px solid #F7E6A2',
                    borderRadius:'6px', padding:'10px 12px', fontSize:'0.8rem',
                    color:'#7A5C00', lineHeight:1.5, marginTop:'14px' },
    list:         { marginTop:'16px', borderTop:'1px solid #eee' },
    row:          { display:'flex', justifyContent:'space-between',
                    alignItems:'center', gap:'12px', padding:'10px 0',
                    borderBottom:'1px solid #f2f2f2' },
    name:         { fontSize:'0.9rem', color:'#333', fontWeight:600 },
    meta:         { fontSize:'0.76rem', color:'#999', marginTop:'2px' },
    amounts:      { textAlign:'right', whiteSpace:'nowrap' },
    earned:       { fontSize:'0.9rem', fontWeight:700, color:'#14532D' },
    pending:      { fontSize:'0.76rem', color:'#999' },
    empty:        { fontSize:'0.84rem', color:'#999', lineHeight:1.6,
                    marginTop:'16px', marginBottom:0 },
};
